import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { 
  Clock, 
  Calendar,
  ChevronRight,
  Coffee,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  Star,
  Trash2,
  Plus,
  Minus,
  Target,
  CheckCircle,
  AlertCircle,
  GripVertical,
  Filter,
  Lock,
  CalendarDays
} from 'lucide-react';

const TimeSchedule = ({ 
  selectedTasks = [], 
  onScheduleChange = () => {}, 
  onClose = () => {},
  onTaskDragged = () => {},
  onTaskRemoved = () => {},
  draggedTaskIds = [],
  selectedDate = null,
  initialSchedule = null
}) => {
  const { currentUser } = useAuth();
  const { meetings = [] } = useData();
  
  const timeSlots = [
    { time: '06:00', label: 'בוקר מוקדם', icon: Sunrise, color: 'from-purple-400 to-pink-400' },
    { time: '06:30', label: '', icon: null, color: 'from-purple-400 to-pink-400' },
    { time: '07:00', label: '', icon: Coffee, color: 'from-orange-400 to-yellow-400' },
    { time: '07:30', label: '', icon: null, color: 'from-orange-400 to-yellow-400' },
    { time: '08:00', label: 'תחילת יום', icon: Sun, color: 'from-yellow-400 to-orange-400' },
    { time: '08:30', label: '', icon: null, color: 'from-yellow-400 to-orange-400' },
    { time: '09:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '09:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '10:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '10:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '11:00', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '11:30', label: '', icon: null, color: 'from-blue-400 to-cyan-400' },
    { time: '12:00', label: 'צהריים', icon: Sun, color: 'from-yellow-500 to-orange-500' },
    { time: '12:30', label: '', icon: null, color: 'from-yellow-500 to-orange-500' },
    { time: '13:00', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '13:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '14:00', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '14:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '15:00', label: 'אחר הצהריים', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '15:30', label: '', icon: null, color: 'from-green-400 to-teal-400' },
    { time: '16:00', label: '', icon: null, color: 'from-indigo-400 to-purple-400' },
    { time: '16:30', label: '', icon: null, color: 'from-indigo-400 to-purple-400' },
    { time: '17:00', label: '', icon: Sunset, color: 'from-orange-400 to-pink-400' },
    { time: '17:30', label: '', icon: null, color: 'from-orange-400 to-pink-400' },
    { time: '18:00', label: 'ערב', icon: null, color: 'from-purple-400 to-pink-400' },
    { time: '18:30', label: '', icon: null, color: 'from-purple-400 to-pink-400' },
    { time: '19:00', label: '', icon: Moon, color: 'from-indigo-400 to-blue-400' },
    { time: '19:30', label: '', icon: null, color: 'from-indigo-400 to-blue-400' },
    { time: '20:00', label: '', icon: Star, color: 'from-indigo-500 to-purple-500' },
    { time: '20:30', label: '', icon: null, color: 'from-indigo-500 to-purple-500' },
    { time: '21:00', label: 'לילה', icon: Moon, color: 'from-purple-500 to-indigo-500' }
  ];

  const [schedule, setSchedule] = useState({});
  const [taskDurations, setTaskDurations] = useState({});
  const [availableTasks, setAvailableTasks] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState(new Set());
  const [weeklyBlockedSlots, setWeeklyBlockedSlots] = useState(new Set()); // תיקון: הוספת state חדש
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [alreadyScheduledTasks, setAlreadyScheduledTasks] = useState([]);
  const [weeklyPlanTasks, setWeeklyPlanTasks] = useState([]); // תיקון: הוספת state חדש
  const [scheduledMeetings, setScheduledMeetings] = useState([]); // ישיבות מתוכננות ומאושרות
  
  const dragRef = useRef(null);
  const ghostRef = useRef(null);

  // פונקציה לזיהוי ישיבות מתוכננות ומאושרות ליום הנבחר
  const getScheduledMeetingsForDate = (dateStr) => {
    if (!dateStr || !currentUser) return [];
    
    // מצא ישיבות שמתוכננות לתאריך זה
    const dayMeetings = meetings.filter(meeting => {
      // רק ישיבות מתוכננות
      if (meeting.status !== 'scheduled') return false;
      
      // בדוק אם המשתמש בכלל משתתף בישיבה
      if (!meeting.participantIds || !meeting.participantIds.includes(currentUser.id)) {
        return false;
      }
      
      // בדוק אם המשתמש אישר הגעה
      if (!meeting.confirmedParticipantIds || !meeting.confirmedParticipantIds.includes(currentUser.id)) {
        return false;
      }
      
      // בדוק אם הישיבה היא באותו יום
      const meetingDate = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
      const meetingDateStr = meetingDate.toISOString().split('T')[0];
      
      return meetingDateStr === dateStr;
    });
    
    return dayMeetings;
  };

  // useEffect לטעינת ישיבות מתוכננות
  useEffect(() => {
    if (selectedDate && meetings.length > 0) {
      const dayMeetings = getScheduledMeetingsForDate(selectedDate);
      setScheduledMeetings(dayMeetings);
      
      // חסום את הזמנים של הישיבות
      const meetingSlots = new Set();
      dayMeetings.forEach(meeting => {
        const startDate = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
        const endDate = meeting.endDate?.toDate ? meeting.endDate.toDate() : new Date(meeting.endDate);
        
        // חשב את כל חצאי השעות שהישיבה תופסת
        let currentTime = new Date(startDate);
        while (currentTime < endDate) {
          const timeStr = `${currentTime.getHours().toString().padStart(2, '0')}:${currentTime.getMinutes().toString().padStart(2, '0')}`;
          meetingSlots.add(timeStr);
          currentTime.setMinutes(currentTime.getMinutes() + 30);
        }
      });
      
      // עדכן את החריגים
      setBlockedSlots(prev => {
        const newBlocked = new Set(prev);
        meetingSlots.forEach(slot => newBlocked.add(slot));
        return newBlocked;
      });
    }
  }, [selectedDate, meetings, currentUser]);

  // פונקציה לקבלת ישיבה עבור זמן מסוים
  const getMeetingForSlot = (timeSlot) => {
    if (!scheduledMeetings.length) return null;
    
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotTime = new Date(2024, 0, 1, hours, minutes);
    
    for (const meeting of scheduledMeetings) {
      const startDate = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
      const endDate = meeting.endDate?.toDate ? meeting.endDate.toDate() : new Date(meeting.endDate);
      
      const startTime = new Date(2024, 0, 1, startDate.getHours(), startDate.getMinutes());
      const endTime = new Date(2024, 0, 1, endDate.getHours(), endDate.getMinutes());
      
      if (slotTime >= startTime && slotTime < endTime) {
        return {
          ...meeting,
          isStart: slotTime.getTime() === startTime.getTime(),
          isContinuation: slotTime.getTime() !== startTime.getTime()
        };
      }
    }
    
    return null;
  };

  // תיקון: פונקציה לבדיקת תכנון שבועי לתאריך הנבחר
  const getWeeklyPlanForDate = (dateStr) => {
    if (!dateStr) return null;
    
    const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
    const userPlans = weeklyPlans.filter(p => 
      p.userId === currentUser?.id || p.userName === currentUser?.name
    );
    
    // מצא תכנון שבועי שכולל את התאריך הזה
    for (const plan of userPlans) {
      if (plan.summaryData?.schedule?.[dateStr]) {
        return {
          planId: plan.id,
          daySchedule: plan.summaryData.schedule[dateStr],
          weekStart: plan.summaryData.weekStart
        };
      }
    }
    
    return null;
  };

  // תיקון: פונקציה לבדיקה האם שעה חסומה על ידי תכנון שבועי
// פונקציה משופרת לבדיקה האם שעה חסומה על ידי תכנון שבועי
const isTimeBlockedByWeeklyPlan = (timeSlot) => {
  if (!selectedDate) return false;
  
  const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
  const userPlans = weeklyPlans.filter(p => 
    p.userId === currentUser?.id || p.userName === currentUser?.name
  );
  
  // מצא את התכנון השבועי שמכיל את התאריך הנבחר
  for (const plan of userPlans) {
    if (plan.summaryData?.schedule?.[selectedDate]) {
      const daySchedule = plan.summaryData.schedule[selectedDate];
      
      // בדיקה ישירה
      if (daySchedule[timeSlot]) {
        return true;
      }
      
      // בדיקת המשך משימות
      const times = Object.keys(daySchedule).sort();
      for (const time of times) {
        if (time < timeSlot && daySchedule[time]) {
          const task = daySchedule[time];
          const duration = task.duration || 1;
          
          const startHour = parseInt(time.split(':')[0]);
          const startMinute = parseInt(time.split(':')[1]);
          const endTime = new Date(2024, 0, 1, startHour, startMinute);
          endTime.setMinutes(endTime.getMinutes() + duration * 60);
          
          const currentHour = parseInt(timeSlot.split(':')[0]);
          const currentMinute = parseInt(timeSlot.split(':')[1]);
          const currentTime = new Date(2024, 0, 1, currentHour, currentMinute);
          
          if (currentTime < endTime) {
            return true;
          }
        }
      }
    }
  }
  
  return false;
};

  // תיקון: פונקציה לקבלת משימה מתכנון שבועי
  const getWeeklyTaskForSlot = (timeSlot) => {
    if (!selectedDate) return null;
    
    const weeklyPlan = getWeeklyPlanForDate(selectedDate);
    if (!weeklyPlan?.daySchedule) return null;
    
    // משימה ישירה
    if (weeklyPlan.daySchedule[timeSlot]) {
      return {
        ...weeklyPlan.daySchedule[timeSlot],
        source: 'weekly',
        isWeeklyTask: true
      };
    }
    
    // המשך משימה
    const times = Object.keys(weeklyPlan.daySchedule).sort();
    for (const time of times) {
      if (time < timeSlot && weeklyPlan.daySchedule[time]) {
        const task = weeklyPlan.daySchedule[time];
        const duration = task.duration || 1;
        
        const startHour = parseInt(time.split(':')[0]);
        const startMinute = parseInt(time.split(':')[1]);
        const endTime = new Date(2024, 0, 1, startHour, startMinute);
        endTime.setMinutes(endTime.getMinutes() + duration * 60);
        
        const currentHour = parseInt(timeSlot.split(':')[0]);
        const currentMinute = parseInt(timeSlot.split(':')[1]);
        const currentTime = new Date(2024, 0, 1, currentHour, currentMinute);
        
        if (currentTime < endTime) {
          return {
            ...task,
            source: 'weekly',
            isWeeklyTask: true,
            isContinuation: true
          };
        }
      }
    }
    
    return null;
  };

  // בדיקה אם משימה כבר תוכננה בתאריך אחר
  const checkIfTaskAlreadyScheduled = (taskId) => {
    try {
      // בדיקת תכנונים יומיים
      const dailyPlans = JSON.parse(localStorage.getItem('dailyPlans') || '[]');
      for (const plan of dailyPlans) {
        // דלג על התאריך הנוכחי
        if (selectedDate && plan.date === selectedDate) continue;
        
        if (plan.schedule) {
          for (const slot of Object.values(plan.schedule)) {
            if (slot?.task?.id === taskId) {
              return { type: 'daily', date: plan.date };
            }
          }
        }
      }

      // בדיקת תכנונים שבועיים
      const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
      for (const plan of weeklyPlans) {
        if (plan.summaryData?.schedule) {
          for (const [dateStr, daySchedule] of Object.entries(plan.summaryData.schedule)) {
            // דלג על התאריך הנוכחי
            if (selectedDate && dateStr === selectedDate) continue;
            
            for (const task of Object.values(daySchedule || {})) {
              if (task?.id === taskId) {
                return { type: 'weekly', date: dateStr };
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking scheduled tasks:', error);
    }
    
    return null;
  };

  // אתחול
  useEffect(() => {
    const emptySchedule = {};
    timeSlots.forEach(slot => {
      emptySchedule[slot.time] = null;
    });
    
    // אם יש schedule קיים (מצב עריכה), טען אותו
    if (initialSchedule && Object.keys(initialSchedule).length > 0) {
      const mergedSchedule = { ...emptySchedule, ...initialSchedule };
      setSchedule(mergedSchedule);
      
      // טען את המשכי הזמן מה-schedule הקיים
      const durations = {};
      Object.values(initialSchedule).forEach(slot => {
        if (slot?.task) {
          const taskId = slot.task.id || slot.task.taskId;
          durations[taskId] = slot.duration || 1;
        }
      });
      
      // הוסף גם משימות שלא תוכננו עדיין
      selectedTasks.forEach(task => {
        if (!durations[task.id]) {
          durations[task.id] = 1;
        }
      });
      
      setTaskDurations(durations);
    } else {
      setSchedule(emptySchedule);
      
      const durations = {};
      selectedTasks.forEach(task => {
        durations[task.id] = 1;
      });
      setTaskDurations(durations);
    }
  }, [initialSchedule]);

  // תיקון: עדכון בדיקת שעות חסומות מתכנון שבועי
  useEffect(() => {
    if (selectedDate) {
      const weeklyBlocked = new Set();
      const weeklyPlan = getWeeklyPlanForDate(selectedDate);
      const weeklyTasks = [];
      
      if (weeklyPlan?.daySchedule) {
        Object.keys(weeklyPlan.daySchedule).forEach(time => {
          weeklyBlocked.add(time);
          
          const task = weeklyPlan.daySchedule[time];
          if (task) {
            weeklyTasks.push({
              ...task,
              scheduledTime: time,
              source: 'weekly'
            });
            
            // הוסף גם שעות המשך
            const duration = task.duration || 1;
            if (duration > 0.5) {
              const timeIndex = timeSlots.findIndex(ts => ts.time === time);
              const slotsToBlock = Math.ceil(duration * 2) - 1;
              for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
                weeklyBlocked.add(timeSlots[timeIndex + i].time);
              }
            }
          }
        });
      }
      
      setWeeklyBlockedSlots(weeklyBlocked);
      setWeeklyPlanTasks(weeklyTasks);
    }
  }, [selectedDate]);

  // עדכון משימות זמינות עם בדיקת תכנונים קיימים
  useEffect(() => {
    const scheduledTaskIds = new Set();
    const alreadyScheduled = [];
    
    // משימות שכבר בלוח הנוכחי
    Object.values(schedule).forEach(task => {
      if (task) scheduledTaskIds.add(task.id);
    });
    
    // משימות מתכנון שבועי לאותו תאריך
    weeklyPlanTasks.forEach(task => {
      if (task.id) {
        scheduledTaskIds.add(task.id);
      }
    });
    
    // משימות שתוכננו בתאריכים אחרים
    selectedTasks.forEach(task => {
      const scheduledInfo = checkIfTaskAlreadyScheduled(task.id);
      if (scheduledInfo) {
        alreadyScheduled.push({
          ...task,
          scheduledDate: scheduledInfo.date,
          scheduledType: scheduledInfo.type
        });
        scheduledTaskIds.add(task.id);
      }
    });
    
    setAlreadyScheduledTasks(alreadyScheduled);
    
    const unscheduledTasks = selectedTasks.filter(task => 
      !scheduledTaskIds.has(task.id) && !draggedTaskIds.includes(task.id)
    );
    
    setAvailableTasks(unscheduledTasks);
  }, [schedule, selectedTasks, draggedTaskIds, selectedDate, weeklyPlanTasks]);

  // חישוב חסימות
  useEffect(() => {
    const blocked = new Set();
    Object.entries(schedule).forEach(([time, task]) => {
      if (task && taskDurations[task.id] > 0.5) {
        const timeIndex = timeSlots.findIndex(ts => ts.time === time);
        const slotsToBlock = Math.ceil(taskDurations[task.id] * 2) - 1;
        for (let i = 1; i <= slotsToBlock && timeIndex + i < timeSlots.length; i++) {
          blocked.add(timeSlots[timeIndex + i].time);
        }
      }
    });
    setBlockedSlots(blocked);
  }, [schedule, taskDurations]);

  // פונקציות גרירה
  const handleDragStart = (e, task, from) => {
    setDraggedTask(task);
    setDraggedFrom(from);
    setIsDragging(true);
    dragRef.current = task;
    
    const ghost = document.createElement('div');
    ghost.className = 'bg-white shadow-xl p-3 rounded-lg opacity-90 pointer-events-none';
    ghost.innerHTML = `<div class="font-medium">${task.title}</div>`;
    ghost.style.position = 'absolute';
    ghost.style.width = '250px';
    ghost.style.zIndex = '9999';
    ghost.style.left = '-9999px';
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 125, 20);
    
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDraggedFrom(null);
    setDropTarget(null);
    setIsDragging(false);
    dragRef.current = null;
    
    if (ghostRef.current) {
      document.body.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, targetId) => {
    e.preventDefault();
    setDropTarget(targetId);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

const handleDrop = (e, targetId) => {
  e.preventDefault();
  
  if (!draggedTask || !draggedFrom) return;

  const newSchedule = { ...schedule };

  // מקור: רשימת משימות
  if (draggedFrom === 'available') {
    if (targetId.startsWith('time-')) {
      const time = targetId.replace('time-', '');
      
      // בדיקת התנגשות עם תכנון שבועי
      if (weeklyBlockedSlots.has(time)) {
        alert('❌ לא ניתן להוסיף משימה - כבר קיימת משימה מתכנון שבועי באותה שעה');
        handleDragEnd();
        return;
      }
      
      // בדיקה אם כבר יש משימה בשעה הזו
      if (newSchedule[time]) {
        alert('❌ כבר קיימת משימה בשעה זו! בחר שעה פנויה.');
        handleDragEnd();
        return;
      }
      
      // בדיקה אם השעה חסומה על ידי משימה ארוכה
      if (blockedSlots.has(time)) {
        alert('❌ השעה הזו תפוסה על ידי המשך של משימה אחרת');
        handleDragEnd();
        return;
      }
      
      newSchedule[time] = draggedTask;
      
      // חשוב! הוסף את ה-ID לדיקשנרי של משכי הזמן
      if (!taskDurations[draggedTask.id]) {
        setTaskDurations(prev => ({
          ...prev,
          [draggedTask.id]: 1
        }));
      }
      
      if (onTaskDragged) {
        onTaskDragged(draggedTask.id, time);
      }
    }
  }
  // מקור: שעה בלוח
  else if (draggedFrom.startsWith('time-')) {
    const sourceTime = draggedFrom.replace('time-', '');
    
    // יעד: רשימת משימות
    if (targetId === 'available') {
      newSchedule[sourceTime] = null;
      if (onTaskRemoved) {
        onTaskRemoved(draggedTask.id);
      }
    }
    // יעד: שעה אחרת
    else if (targetId.startsWith('time-')) {
      const destTime = targetId.replace('time-', '');
      
      // בדיקת התנגשות עם תכנון שבועי
      if (weeklyBlockedSlots.has(destTime)) {
        alert('❌ לא ניתן להעביר משימה - כבר קיימת משימה מתכנון שבועי באותה שעה');
        handleDragEnd();
        return;
      }
      
      // בדיקה אם כבר יש משימה בשעה היעד
      if (newSchedule[destTime] && destTime !== sourceTime) {
        alert('❌ כבר קיימת משימה בשעה זו! בחר שעה פנויה.');
        handleDragEnd();
        return;
      }
      
      // בדיקה אם השעה חסומה
      if (blockedSlots.has(destTime) && destTime !== sourceTime) {
        alert('❌ השעה הזו תפוסה על ידי המשך של משימה אחרת');
        handleDragEnd();
        return;
      }
      
      newSchedule[sourceTime] = null;
      newSchedule[destTime] = draggedTask;
    }
  }

  setSchedule(newSchedule);
  handleDragEnd();
};
const updateTaskDuration = (taskId, time, change) => {
  // וודא שיש taskId ו-taskDurations מאותחל
  if (!taskId || !taskDurations) {
    console.error('Missing taskId or taskDurations not initialized');
    return;
  }
  
  const currentDuration = taskDurations[taskId] || 1;
  const newDuration = Math.max(0.5, Math.min(4, currentDuration + change));
  
  const timeIndex = timeSlots.findIndex(ts => ts.time === time);
  const slotsNeeded = Math.ceil(newDuration * 2);
  
  let canExpand = true;
  for (let i = 1; i < slotsNeeded && timeIndex + i < timeSlots.length; i++) {
    const nextTime = timeSlots[timeIndex + i]?.time;
    if (nextTime) {
      // בדוק תכנון שבועי
      if (weeklyBlockedSlots.has(nextTime)) {
        canExpand = false;
        break;
      }
      // בדוק משימות אחרות
      if (schedule[nextTime]) {
        canExpand = false;
        break;
      }
    }
  }

  if (canExpand || newDuration < currentDuration) {
    setTaskDurations(prev => ({
      ...prev,
      [taskId]: newDuration
    }));
  } else {
    alert('❌ לא ניתן להרחיב את המשימה - יש משימות חוסמות');
  }
};

  const removeTask = (time) => {
    const newSchedule = { ...schedule };
    const task = newSchedule[time];
    
    if (task) {
      newSchedule[time] = null;
      setSchedule(newSchedule);
      
      if (onTaskRemoved) {
        onTaskRemoved(task.id);
      }
    }
  };

  const formatDuration = (duration) => {
    if (duration === 0.5) return 'חצי שעה';
    if (duration === 1) return 'שעה';
    if (duration === 1.5) return 'שעה וחצי';
    if (duration % 1 === 0) return `${duration} שעות`;
    return `${Math.floor(duration)} וחצי שעות`;
  };

  const handleNext = () => {
    const formattedSchedule = {};
    Object.entries(schedule).forEach(([time, task]) => {
      if (task) {
        formattedSchedule[time] = {
          task: task,
          duration: taskDurations[task.id] || 1
        };
      }
    });
    onScheduleChange(formattedSchedule);
  };

  const stats = {
    scheduled: Object.values(schedule).filter(task => task !== null).length,
    total: selectedTasks.length,
    totalHours: Object.values(schedule).reduce((sum, task) => 
      sum + (task ? (taskDurations[task.id] || 1) : 0), 0
    ),
    alreadyScheduled: alreadyScheduledTasks.length,
    weeklyTasks: weeklyPlanTasks.length
  };

  const getPermissionMessage = () => {
    const role = currentUser?.role;
    const teamName = currentUser?.teamName;
    const deptName = currentUser?.departmentName;

    switch (role) {
      case 'team_manager':
        return `מוצגות משימות של צוות: ${teamName || 'לא מוגדר'}`;
      case 'dept_manager':
        return `מוצגות משימות של מחלקת: ${deptName || 'לא מוגדר'}`;
      case 'employee':
        return 'מוצגות המשימות שלך בלבד';
      case 'org_admin':
      case 'ceo':
      case 'deputy_ceo':
        return 'מוצגות כל המשימות בארגון';
      default:
        return 'מוצגות משימות לפי הרשאות';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          ארגון המשימות בלוח הזמנים
        </h2>
        <p className="text-gray-600 mb-4">
          גרור משימות לשעות המתאימות - משימה אחת בלבד לכל שעה
        </p>
        
        {selectedDate && (
          <div className="mb-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-sm font-medium text-blue-800">
              תכנון ליום: {new Date(selectedDate).toLocaleDateString('he-IL')}
            </p>
          </div>
        )}
        
        {/* הודעת הרשאות */}
        <div className="mb-4 p-3 bg-gray-100 rounded-lg flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <span className="text-sm text-gray-800">
            {getPermissionMessage()}
          </span>
        </div>
        
        {/* התראה על ישיבות מתוכננות */}
        {scheduledMeetings.length > 0 && (
          <div className="mb-4 p-3 bg-blue-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                📅 {scheduledMeetings.length} ישיבות מתוכננות לתאריך זה
              </span>
            </div>
            <div className="text-xs text-blue-700">
              {scheduledMeetings.map((meeting, idx) => (
                <div key={idx} className="mt-1">
                  • {meeting.subject} - {new Date(meeting.startDate?.toDate ? meeting.startDate.toDate() : meeting.startDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* תיקון: התראה על משימות מתכנון שבועי */}
        {stats.weeklyTasks > 0 && (
          <div className="mb-4 p-3 bg-purple-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">
                {stats.weeklyTasks} משימות מתכנון שבועי לתאריך זה
              </span>
            </div>
            <div className="text-xs text-purple-700">
              שעות אלו חסומות ולא ניתן להוסיף בהן משימות חדשות
            </div>
          </div>
        )}
        
        {/* התראה על משימות שכבר תוכננו */}
        {stats.alreadyScheduled > 0 && (
          <div className="mb-4 p-3 bg-yellow-100 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">
                {stats.alreadyScheduled} משימות כבר תוכננו בתאריכים אחרים
              </span>
            </div>
            <div className="text-xs text-yellow-700">
              {alreadyScheduledTasks.slice(0, 3).map((task, index) => (
                <div key={index}>
                  • {task.title} - {new Date(task.scheduledDate).toLocaleDateString('he-IL')}
                </div>
              ))}
              {alreadyScheduledTasks.length > 3 && (
                <div>ועוד {alreadyScheduledTasks.length - 3} משימות...</div>
              )}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
            <div className="text-sm text-gray-600">משימות מתוכננות</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.totalHours}</div>
            <div className="text-sm text-gray-600">שעות מתוכננות</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {availableTasks.length}
            </div>
            <div className="text-sm text-gray-600">זמינות לתכנון</div>
          </div>
        </div>
      </div>

      <div className="flex gap-6 flex-1" style={{ maxHeight: '500px' }}>
        {/* רשימת משימות */}
        <div className="w-1/3">
          <div className="bg-white rounded-xl shadow-lg p-6 h-full overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-500" />
              משימות זמינות ({availableTasks.length})
            </h3>
            
            <div
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, 'available')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'available')}
              className={`flex-1 overflow-y-auto space-y-2 p-2 rounded-lg transition-colors ${
                dropTarget === 'available' ? 'bg-gray-100 ring-2 ring-blue-400' : ''
              }`}
              style={{ maxHeight: '400px' }}
            >
              {availableTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p>כל המשימות תוכננו!</p>
                  {stats.alreadyScheduled > 0 && (
                    <p className="text-xs mt-2">
                      ({stats.alreadyScheduled} משימות תוכננו בתאריכים אחרים)
                    </p>
                  )}
                </div>
              ) : (
                availableTasks.map((task) => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, 'available')}
                    onDragEnd={handleDragEnd}
                    className={`p-3 bg-gradient-to-r cursor-move ${
                      task.priority === 'high' 
                        ? 'from-red-50 to-pink-50 border-red-200' 
                        : task.priority === 'medium'
                        ? 'from-yellow-50 to-orange-50 border-yellow-200'
                        : 'from-green-50 to-teal-50 border-green-200'
                    } border rounded-lg transition-all hover:shadow-md ${
                      draggedTask?.id === task.id ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 mt-1">{task.description}</div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.priority === 'high'
                              ? 'bg-red-100 text-red-700'
                              : task.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {task.priority === 'high' ? 'דחוף' : 
                             task.priority === 'medium' ? 'בינוני' : 'רגיל'}
                          </span>
                          {task.projectName && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              {task.projectName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* לוח זמנים */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-lg p-6 h-full overflow-hidden">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              לוח זמנים יומי
            </h3>
            
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <div className="space-y-2">
                {timeSlots.map((slot) => {
                  const hasWeeklyTask = isTimeBlockedByWeeklyPlan(slot.time);
                  const weeklyTask = getWeeklyTaskForSlot(slot.time);
                  const meetingForSlot = getMeetingForSlot(slot.time);
                  const isBlocked = blockedSlots.has(slot.time);
                  const task = schedule[slot.time];
                  const canDrop = !isBlocked && !task && !hasWeeklyTask && !meetingForSlot;
                  
                  return (
                    <div
                      key={slot.time}
                      onDragOver={canDrop ? handleDragOver : null}
                      onDragEnter={canDrop ? (e) => handleDragEnter(e, `time-${slot.time}`) : null}
                      onDragLeave={canDrop ? handleDragLeave : null}
                      onDrop={canDrop ? (e) => handleDrop(e, `time-${slot.time}`) : null}
                      className={`flex items-stretch gap-3 p-3 rounded-lg transition-all ${
                        isBlocked 
                          ? 'bg-gray-100 opacity-50' 
                          : meetingForSlot
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : hasWeeklyTask
                          ? 'bg-purple-50 border-l-4 border-purple-400'
                          : task
                          ? 'bg-gray-50'
                          : dropTarget === `time-${slot.time}`
                          ? 'bg-blue-50 scale-[1.02] ring-2 ring-blue-400' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="w-24 flex items-center gap-2">
                        {slot.icon && (
                          <slot.icon className={`w-5 h-5 bg-gradient-to-r ${slot.color} text-white rounded p-1`} />
                        )}
                        <span className="font-mono font-bold text-gray-700">{slot.time}</span>
                      </div>
                      
                      <div className="flex-1" style={{ minHeight: '50px' }}>
                        {isBlocked ? (
                          <div className="text-center text-gray-400 text-sm py-2">
                            (המשך משימה)
                          </div>
                        ) : meetingForSlot ? (
                          meetingForSlot.isContinuation ? (
                            <div className="p-3 bg-blue-100 border-2 border-blue-400 rounded-lg">
                              <div className="text-center text-blue-600 text-sm">
                                ↓ המשך ישיבה ↓
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-blue-100 border-2 border-blue-400 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <CalendarDays className="w-5 h-5 text-blue-600" />
                                <div className="font-bold text-blue-900">
                                  📅 {meetingForSlot.subject}
                                </div>
                              </div>
                              <div className="text-sm text-blue-700 mb-1">
                                🎯 {meetingForSlot.purpose}
                              </div>
                              {meetingForSlot.location && (
                                <div className="text-xs text-blue-600 mb-1">
                                  📍 {meetingForSlot.location}
                                </div>
                              )}
                              <div className="text-xs text-blue-600 flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                {new Date(meetingForSlot.startDate?.toDate ? meetingForSlot.startDate.toDate() : meetingForSlot.startDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} - 
                                {new Date(meetingForSlot.endDate?.toDate ? meetingForSlot.endDate.toDate() : meetingForSlot.endDate).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          )
                        ) : weeklyTask ? (
                          weeklyTask.isContinuation ? (
                            <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded-lg opacity-75">
                              <div className="text-center text-purple-600 text-sm">
                                ↓ המשך משימה מתכנון שבועי ↓
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded-lg opacity-75">
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-purple-600" />
                                <div className="font-medium text-gray-700">
                                  {weeklyTask.title}
                                </div>
                              </div>
                              <div className="text-xs text-purple-600 mt-1">
                                📅 מתכנון שבועי - לא ניתן לערוך
                              </div>
                              {weeklyTask.duration && (
                                <div className="text-xs text-purple-600 mt-1">
                                  משך: {formatDuration(weeklyTask.duration)}
                                </div>
                              )}
                            </div>
                          )
                        ) : task ? (
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, task, `time-${slot.time}`)}
                            onDragEnd={handleDragEnd}
                            className={`p-3 bg-white border-2 rounded-lg cursor-move ${
                              task.priority === 'high'
                                ? 'border-red-300 bg-red-50'
                                : task.priority === 'medium'
                                ? 'border-yellow-300 bg-yellow-50'
                                : 'border-green-300 bg-green-50'
                            } ${draggedTask?.id === task.id ? 'opacity-50' : ''}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex items-start gap-2 flex-1">
                                <GripVertical className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {task.title}
                                  </div>
                                  {task.projectName && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {task.projectName}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 mr-2">
                                <div className="bg-white border border-gray-200 rounded-lg px-2 py-1">
                                  <div className="text-xs text-gray-500 mb-1">משך זמן:</div>
                                  <div className="flex items-center gap-1">
                                 <button
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();
    const taskId = task.id || task.taskId || `temp-${Date.now()}`;
    
    // וודא שהמשימה קיימת ב-taskDurations
    if (!taskDurations[taskId]) {
      setTaskDurations(prev => ({
        ...prev,
        [taskId]: 1
      }));
    }
    
    updateTaskDuration(taskId, slot.time, -0.5);
  }}
  className="p-1 hover:bg-gray-200 rounded transition-colors"
>
  <Minus className="w-3 h-3" />
</button>
                                    <span className="text-sm font-bold px-2 min-w-[80px] text-center">
                                      {formatDuration(taskDurations[task.id] || 1)}
                                    </span>
                              <button
  onClick={(e) => {
    e.stopPropagation();
    e.preventDefault();
    const taskId = task.id || task.taskId || `temp-${Date.now()}`;
    
    // וודא שהמשימה קיימת ב-taskDurations
    if (!taskDurations[taskId]) {
      setTaskDurations(prev => ({
        ...prev,
        [taskId]: 1
      }));
    }
    
    updateTaskDuration(taskId, slot.time, 0.5);
  }}
  className="p-1 hover:bg-gray-200 rounded transition-colors"
>
  <Plus className="w-3 h-3" />
</button>
                                  </div>
                                </div>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTask(slot.time);
                                  }}
                                  className="p-2 hover:bg-red-100 rounded text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-12 border-2 border-dashed border-gray-300 rounded-lg text-gray-400">
                            גרור משימה לכאן
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* כפתור המשך */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={handleNext}
          disabled={stats.scheduled === 0}
          className={`px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center gap-3 ${
            stats.scheduled > 0
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          המשך לסיכום
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default TimeSchedule;